import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MenuItemProps, MenuProps, UseMenuNavigationParams, UseMenuNavigationReturn } from './types';

const TYPE_AHEAD_RESET_MS = 500;

/**
 * Motor de *roving tabindex* para el patrón `menu` de WAI-ARIA APG.
 *
 * Comportamiento:
 *  - ArrowDown/ArrowUp mueven el foco a la siguiente/anterior fila ENFOCABLE
 *    (salta separadores y títulos), con envoltura (`shouldWrap`, default true).
 *  - Home/End enfocan la primera/última fila enfocable.
 *  - Type-ahead: acumula caracteres imprimibles en un buffer que se reinicia a
 *    los ~500 ms; busca la primera fila cuya etiqueta empiece por el buffer
 *    (case-insensitive), escaneando desde `activeIndex + 1` con envoltura.
 *  - Enter/Space: `preventDefault` + `onActivate(activeIndex)` salvo fila
 *    deshabilitada. Las filas deshabilitadas SIGUEN en el anillo de foco
 *    (`aria-disabled="true"`, activación no-op) — patrón menú de APG.
 *  - Exactamente una fila tiene `tabIndex=0` (la de `activeIndex`); el resto -1.
 *
 * El hook no controla la activación por click ni el render: eso lo compone el
 * componente consumidor.
 */
function useMenuNavigation({
  isItemDisabled,
  isItemFocusable,
  isItemSubmenuTrigger,
  itemCount,
  onActivate,
  onCloseSubmenu,
  shouldAutoFocusFirstItem = false,
  shouldWrap = true,
  typeAheadLabels,
}: UseMenuNavigationParams): UseMenuNavigationReturn {
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const typeAheadBufferRef = useRef<string>('');
  const typeAheadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const findFirstFocusable = useCallback((): number => {
    for (let index = 0; index < itemCount; index += 1) {
      if (isItemFocusable(index)) return index;
    }
    return -1;
  }, [isItemFocusable, itemCount]);

  const findLastFocusable = useCallback((): number => {
    for (let index = itemCount - 1; index >= 0; index -= 1) {
      if (isItemFocusable(index)) return index;
    }
    return -1;
  }, [isItemFocusable, itemCount]);

  const [activeIndex, setActiveIndex] = useState<number>(() => findFirstFocusable());

  const focusIndex = useCallback((index: number): void => {
    if (index < 0) return;
    setActiveIndex(index);
    // `preventScroll` evita el salto de scroll: el panel del menú va portalado a
    // <body> (MUI Popper) y un `.focus()` sin más desplazaría la página.
    itemRefs.current[index]?.focus({ preventScroll: true });
  }, []);

  const focusFirstItem = useCallback((): void => {
    focusIndex(findFirstFocusable());
  }, [findFirstFocusable, focusIndex]);

  useEffect(() => {
    if (shouldAutoFocusFirstItem) {
      focusFirstItem();
    }
    // Solo al montar: la apertura de un menú enfoca su primera fila una vez.
  }, []);

  // Reconcilia el índice activo cuando cambian las filas (carga async, filas
  // que desaparecen o dejan de ser enfocables). Sin esto ninguna fila queda
  // con tabIndex=0 y el menú deja de ser alcanzable con Tab.
  useEffect(() => {
    if (activeIndex < 0 || activeIndex >= itemCount || !isItemFocusable(activeIndex)) {
      setActiveIndex(findFirstFocusable());
    }
  }, [activeIndex, findFirstFocusable, isItemFocusable, itemCount]);

  useEffect(
    () => () => {
      if (typeAheadTimeoutRef.current) clearTimeout(typeAheadTimeoutRef.current);
    },
    [],
  );

  // Mueve el foco `step` filas enfocables desde `activeIndex`, envolviendo si
  // procede. El for cap a `itemCount` iteraciones evita bucles infinitos cuando
  // no hay ninguna fila enfocable.
  const moveFocus = useCallback(
    (step: number): void => {
      if (itemCount === 0) return;
      let next = activeIndex;
      for (let attempt = 0; attempt < itemCount; attempt += 1) {
        next += step;
        if (next < 0) {
          if (!shouldWrap) return;
          next = itemCount - 1;
        } else if (next >= itemCount) {
          if (!shouldWrap) return;
          next = 0;
        }
        if (isItemFocusable(next)) {
          focusIndex(next);
          return;
        }
      }
    },
    [activeIndex, focusIndex, isItemFocusable, itemCount, shouldWrap],
  );

  const runTypeAhead = useCallback(
    (char: string): void => {
      if (!typeAheadLabels) return;

      if (typeAheadTimeoutRef.current) clearTimeout(typeAheadTimeoutRef.current);
      typeAheadTimeoutRef.current = setTimeout(() => {
        typeAheadBufferRef.current = '';
      }, TYPE_AHEAD_RESET_MS);

      typeAheadBufferRef.current += char.toLowerCase();
      const buffer = typeAheadBufferRef.current;

      for (let attempt = 1; attempt <= itemCount; attempt += 1) {
        const candidate = (activeIndex + attempt) % itemCount;
        if (!isItemFocusable(candidate)) continue;
        const label = typeAheadLabels[candidate];
        if (label?.toLowerCase().startsWith(buffer)) {
          focusIndex(candidate);
          return;
        }
      }
    },
    [activeIndex, focusIndex, isItemFocusable, itemCount, typeAheadLabels],
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>): void => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveFocus(1);
          return;
        case 'ArrowUp':
          event.preventDefault();
          moveFocus(-1);
          return;
        case 'Home':
          event.preventDefault();
          focusIndex(findFirstFocusable());
          return;
        case 'End':
          event.preventDefault();
          focusIndex(findLastFocusable());
          return;
        case 'ArrowRight':
          // Solo despliega si la fila activa abre un submenú; en el resto de
          // menús ArrowRight se ignora (menú vertical, no menubar).
          if (isItemSubmenuTrigger?.(activeIndex)) {
            event.preventDefault();
            onActivate(activeIndex, event);
          }
          return;
        case 'ArrowLeft':
          // Vuelve al menú padre. Solo actúa en un submenú (quien pasa `onCloseSubmenu`).
          if (onCloseSubmenu) {
            event.preventDefault();
            onCloseSubmenu();
          }
          return;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (!isItemDisabled?.(activeIndex)) {
            onActivate(activeIndex, event);
          }
          return;
        default:
          // Type-ahead: solo caracteres imprimibles sueltos, sin modificadores.
          if (event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
            runTypeAhead(event.key);
          }
      }
    },
    [
      activeIndex,
      findFirstFocusable,
      findLastFocusable,
      focusIndex,
      isItemDisabled,
      isItemSubmenuTrigger,
      moveFocus,
      onActivate,
      onCloseSubmenu,
      runTypeAhead,
    ],
  );

  const getMenuProps = useCallback(
    (): MenuProps => ({
      'aria-orientation': 'vertical',
      onKeyDown,
      role: 'menu',
    }),
    [onKeyDown],
  );

  const getItemProps = useCallback(
    (index: number): MenuItemProps => {
      const isDisabled = isItemDisabled?.(index) ?? false;
      // Sincroniza el índice al recibir foco por click/Tab: sin esto las
      // flechas navegarían desde una posición obsoleta tras un click.
      function handleFocus(): void {
        setActiveIndex(index);
      }
      return {
        ...(isDisabled ? { 'aria-disabled': true } : {}),
        onFocus: handleFocus,
        ref: (node: HTMLElement | null) => {
          itemRefs.current[index] = node;
        },
        role: 'menuitem',
        tabIndex: index === activeIndex ? 0 : -1,
      };
    },
    [activeIndex, isItemDisabled],
  );

  return {
    activeIndex,
    focusFirstItem,
    getItemProps,
    getMenuProps,
    setActiveIndex,
  };
}

export default useMenuNavigation;
